import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBarsFetcher } from '../../hooks/useTradeStationQueries';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { hasResolvableBrokerIdentity, isBrokerLinked, supportsMarketData } from '../../utils/brokerConnection';
import { loadBarsPaged } from '../data/loadBarsPaged';
import { formatBarSpan } from '../engine/bars';

export function useBacktestBars({
  ticker,
  interval,
  unit,
  firstdate,
  lastdate,
  sessionTemplate = 'Default',
  enabled = true,
  extraTimeframes = [],
} = {}) {
  const fetchBars = useBarsFetcher();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const canFetch = supportsMarketData(user, selectedAccount);
  const waitingForBroker = !user
    || (Boolean(user?.token)
      && isBrokerLinked(user)
      && !canFetch
      && (!selectedAccount || !hasResolvableBrokerIdentity(selectedAccount)));
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [progress, setProgress] = useState(0);
  const requestId = useRef(0);

  // Extra timeframe series, keyed the same way as the `extraTimeframes` entries' `key`.
  const [timeframes, setTimeframes] = useState({});
  const [tfLoading, setTfLoading] = useState({});
  const [tfError, setTfError] = useState({});
  const tfRequestId = useRef(0);

  // Fingerprint the extra timeframe requests so the fetch effect only re-runs when the
  // actual set of requested timeframes changes (e.g. switching strategies), not on every render.
  const extraTimeframesKey = useMemo(
    () => JSON.stringify((extraTimeframes || []).map((tf) => ({ key: tf.key, interval: tf.interval, unit: tf.unit }))),
    [extraTimeframes],
  );

  const reload = useCallback(async () => {
    if (!enabled || !canFetch || !ticker || !interval || !unit || !firstdate || !lastdate) {
      setBars([]);
      setError(null);
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setProgress(0);
    try {
      const result = await loadBarsPaged({
        fetchBars,
        ticker,
        interval,
        unit,
        firstdate,
        lastdate,
        sessionTemplate,
        onProgress: ({ bars: count }) => {
          if (requestId.current === id) setProgress(count);
        },
      });
      if (requestId.current !== id) return;
      setBars(result.bars);
      setTruncated(Boolean(result.truncated));
      setProgress(result.bars.length);
    } catch (err) {
      if (requestId.current !== id) return;
      setBars([]);
      setError(err?.message || 'Could not load bars');
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [canFetch, enabled, fetchBars, firstdate, interval, lastdate, sessionTemplate, ticker, unit]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Fetch each extra timeframe over the same date range as the base series, using the same
  // paged-fetch mechanism. Driven entirely off the `extraTimeframes` array (via its fingerprint)
  // rather than one hook call per timeframe, so hook call counts stay fixed regardless of how
  // many timeframes are requested.
  const reloadTimeframes = useCallback(async () => {
    const requests = extraTimeframes || [];
    if (!enabled || !canFetch || !ticker || !firstdate || !lastdate || !requests.length) {
      setTimeframes({});
      setTfLoading({});
      setTfError({});
      return;
    }
    const id = ++tfRequestId.current;
    const loadingState = {};
    requests.forEach((req) => { loadingState[req.key] = true; });
    setTfLoading(loadingState);
    setTfError({});

    await Promise.all(requests.map(async (req) => {
      try {
        const result = await loadBarsPaged({
          fetchBars,
          ticker,
          interval: req.interval,
          unit: req.unit,
          firstdate,
          lastdate,
          sessionTemplate,
        });
        if (tfRequestId.current !== id) return;
        setTimeframes((prev) => ({ ...prev, [req.key]: result.bars }));
      } catch (err) {
        if (tfRequestId.current !== id) return;
        setTfError((prev) => ({ ...prev, [req.key]: err?.message || 'Could not load bars' }));
      } finally {
        if (tfRequestId.current === id) {
          setTfLoading((prev) => ({ ...prev, [req.key]: false }));
        }
      }
    }));
  }, [canFetch, enabled, extraTimeframes, fetchBars, firstdate, lastdate, sessionTemplate, ticker]);

  useEffect(() => {
    reloadTimeframes();
    // extraTimeframesKey captures the meaningful identity of extraTimeframes; reloadTimeframes
    // itself is stable per that identity (plus the other shared deps already in its own list).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTimeframes, extraTimeframesKey]);

  const tfLoadingKeys = Object.keys(tfLoading).filter((key) => tfLoading[key]);
  const tfErrorKeys = Object.keys(tfError).filter((key) => tfError[key]);

  return {
    bars,
    loading: loading || tfLoadingKeys.length > 0,
    error: error || (tfErrorKeys.length ? `Timeframe ${tfErrorKeys.join(', ')}: ${tfError[tfErrorKeys[0]]}` : null),
    truncated,
    progress,
    canFetch,
    waitingForBroker,
    spanLabel: formatBarSpan(bars),
    reload,
    timeframes,
    timeframesLoading: tfLoadingKeys,
    timeframesError: tfError,
  };
}
